import { ARROW_LEFT, isNavigable } from '../../../utils/keyboard-nav-support';
import { action } from '@ember/object';
import { doesNotExist } from '../../../utils/spds/component-validations/helpers';
import { isEmpty } from '@ember/utils';
import { modifier } from 'ember-modifier';
import { or, reads } from 'macro-decorators';
import { stripBolding } from '../../../modifiers/bold-matching-text';
import { tracked } from '@glimmer/tracking';
import { waitForNext } from '../../../utils/waiters';
import Super from '../core/selectable-menu.gjs';

export default class SpdsInputMenuParent extends Super {
  classesToMap = ['disabled', 'error', 'open', 'required'];

  @tracked triggerComponent;

  @or('isMultiselect', 'search') allowHorizontalNavigation;

  @reads('menuComponent.checkboxGroup') checkboxGroup;
  @reads('checkboxGroup.baseElement') checkboxList;
  @reads('triggerComponent.clearButton') clearButton;
  @reads('triggerComponent.clearFilterComponent.clearButton') clearFilter;
  @reads('args.multiselect') isMultiselect;
  @reads('checkboxGroup.selected', []) selectedCheckboxes;
  @reads('selectAllComponent') selectAllOption;
  @reads('triggerComponent.trigger') trigger;
  @reads('triggerComponent.baseElement') triggerContainer;

  get caretAtEnd() {
    let length = this.trigger?.value?.length ?? 0;

    return this.trigger?.selectionStart === length && this.trigger?.selectionEnd === length;
  }

  get caretAtStart() {
    return this.trigger?.selectionStart === 0 && this.trigger?.selectionEnd === 0;
  }

  get leftNavigableOptions() {
    return isNavigable(this.clearFilter?.parentElement) ? [this.clearFilter] : [];
  }

  get rightNavigableOptions() {
    return isNavigable(this.clearButton) ? [this.clearButton] : [];
  }

  get defaultValue() {
    return this.args.value;
  }

  get helperTextId() {
    return this.composeSubcomponentId('helper-text');
  }

  get labelId() {
    return this.composeSubcomponentId('label');
  }

  get menuCanOpen() {
    return this.value.length >= this.menuOpenThreshold || isEmpty(this.menuOpenThreshold);
  }

  get menuId() {
    return this.composeSubcomponentId('menu');
  }

  get menuType() {
    return this.isMultiselect ? this.multiselectMenuComponent : this.singleSelectMenuComponent;
  }

  get retainSelection() {
    return this.args.retainSelection ?? true;
  }

  get triggerHasFocus() {
    return !!this.trigger && this.trigger === document?.activeElement;
  }

  get triggerId() {
    return this.composeSubcomponentId('trigger');
  }

  get triggerIsTextInput() {
    return this.trigger?.tagName === 'INPUT';
  }

  updateValueFromExternalSource = modifier(async () => {
    let { value } = this.args;

    if (!(this.isCurrentValue(value) && isEmpty(value))) {
      await this.updateValue(value);
    }
  });

  exitToTrigger() {
    this.activeOption = undefined;
    this.focusTrigger();
  }

  focusTrigger() {
    this.trigger.focus();
  }

  getActiveOption() {
    return super.getActiveOption() ?? this.trigger;
  }

  canEnterHorizontal({ key }) {
    if (!(this.triggerHasFocus && this.triggerIsTextInput)) return true;

    return key === ARROW_LEFT ? this.caretAtStart : this.caretAtEnd;
  }

  initialize = async () => {
    await this.updateValue();
  };

  manageSingleSelectItem(target) {
    if (!target) {
      this.menuNavigableOptions.forEach(option => (option.ariaSelected = 'false'));
      this.selected = null;
      return;
    }

    this.updateSelectedItemState('false');
    this.selected = this.retainSelection || target?.id !== this.selected?.id ? target : null;
    this.updateSelectedItemState('true');
  }

  normalizeValue(value) {
    return value || '';
  }

  optionElement(option) {
    return this.isMultiselect ? option.parentElement : option;
  }

  optionMatchesValue(option, value) {
    return [option.dataset.value, option.textContent.trim()].includes(value);
  }

  updateSelectedItemState(state) {
    this.selected?.setAttribute('aria-selected', state);
  }

  async updateValue() {
    let { multiselect, value } = this.args;

    await waitForNext();

    if (multiselect) {
      this.selected = this.selectedCheckboxes;
    } else {
      this.setSelection(value);
    }
  }

  syncExternalSelection = modifier(async () => {
    if (!this.isMultiselect) return;

    let { selectedValues } = this.args;
    if (doesNotExist(selectedValues)) return;

    await waitForNext();

    this.menuNavigableOptions.forEach(option => {
      if (option.id === this.selectAllOption?.id) return;
      option.checked = selectedValues.includes(option.value);
    });

    this.checkboxList?.dispatchEvent(new CustomEvent('sync'));

    this.selected = [...selectedValues];
  });

  clearSelection = () => {
    this.checkboxList.dispatchEvent(new CustomEvent('clear'));

    this.onChange({ values: [] });

    this.trigger.focus();
  };

  handleClose = async () => {
    let shouldClose = (await Promise.resolve(this.args.onClose?.())) ?? true;

    this.open = !shouldClose;

    if (shouldClose) {
      this.removeFocus();
      this.deactivateHorizontalOption();
    }
  };

  handleOpen = () => {
    this.open = true;
    this.args.onOpen?.();
  };

  toggle = () => {
    this.open ? this.handleClose() : this.handleOpen();
  };

  @action
  onChange({ values }) {
    if (this.isMultiselect) {
      this.selected = [...values];
    }

    this.args.onChange?.(...arguments);
  }

  @action
  onSelect({ target }) {
    let selected = target.tagName === 'STRONG' ? target.parentElement : target;

    this.manageSingleSelectItem(selected);

    this.value = this.selected ? stripBolding(this.selected) : '';

    this.handleClose();
    this.trigger.focus();

    this.args.onChange?.(...arguments);
  }
}
